// components/profile/Armoury.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Swords,
  Plus,
  X,
  Trash2,
  Pencil,
  ImagePlus,
  Loader2,
  ShieldAlert,
  Bike,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/services/api";
import { getImageUrl } from "@/utils/imageUrl";

// ─── Shimmer placeholder ────────────────────────────────────
const ShimmerCard = () => (
  <div className="rounded-2xl overflow-hidden bg-gray-200 dark:bg-gray-800 animate-pulse h-72" />
);

// ─── Main component ─────────────────────────────────────────
const Armoury = () => {
  const [gear, setGear] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState(null);

  // form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileRef = useRef(null);

  // ── Fetch ──────────────────────────────────────────────────
  useEffect(() => {
    fetchArmoury();
  }, []);

  const fetchArmoury = async () => {
    try {
      setLoading(true);
      const res = await api.get("/users/me/armoury");
      setGear(res.data?.armoury || []);
    } catch {
      /* toast handled by interceptor */
    } finally {
      setLoading(false);
    }
  };

  // ── Open add / edit modal ──────────────────────────────────
  const openAdd = () => {
    setEditItem(null);
    setTitle("");
    setDescription("");
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setTitle(item.title);
    setDescription(item.description || "");
    setImageFile(null);
    setImagePreview(item.image_url ? getImageUrl(item.image_url) : null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditItem(null);
  };

  // ── Image picker ───────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // ── Submit (add / edit) ────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    setSubmitting(true);
    try {
      if (editItem) {
        let res;
        if (imageFile) {
          const fd = new FormData();
          fd.append("title", title.trim());
          fd.append("description", description.trim());
          fd.append("gear", imageFile);
          res = await api.put(`/users/me/armoury/${editItem._id}`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          res = await api.put(`/users/me/armoury/${editItem._id}`, {
            title,
            description,
          });
        }
        setGear((prev) =>
          prev.map((g) => (g._id === editItem._id ? res.data.gear : g))
        );
      } else {
        // add new with optional image
        const fd = new FormData();
        fd.append("title", title.trim());
        fd.append("description", description.trim());
        if (imageFile) fd.append("gear", imageFile);

        const res = await api.post("/users/me/armoury", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setGear((prev) => [...prev, res.data.gear]);
      }
      closeModal();
    } catch {
      /* interceptor */
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await api.delete(`/users/me/armoury/${deleteTarget._id}`);
      setGear((prev) => prev.filter((g) => g._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch {
      /* interceptor */
    } finally {
      setSubmitting(false);
    }
  };

  // ── Gear card ──────────────────────────────────────────────
  const GearCard = ({ item, index }) => (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, y: -20 }}
      transition={{ duration: 0.4, delay: index * 0.07 }}
      className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50"
      style={{ minHeight: "20rem" }}
    >
      {/* Image area */}
      <div 
        className="relative h-52 overflow-hidden bg-gradient-to-br from-emerald-100 to-teal-50 dark:from-emerald-900/40 dark:to-gray-900"
        onClick={() => {
          if (item.image_url) setFullScreenImage(getImageUrl(item.image_url));
        }}
      >
        {item.image_url ? (
          <img
            src={getImageUrl(item.image_url)}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 cursor-zoom-in"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Bike className="w-16 h-16 text-emerald-300 dark:text-emerald-700 opacity-60" />
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

        {/* Action buttons — appear on hover */}
        <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEdit(item);
            }}
            className="p-2 rounded-full bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-200 shadow-md hover:bg-emerald-500 hover:text-white transition-colors"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(item);
            }}
            className="p-2 rounded-full bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-200 shadow-md hover:bg-red-500 hover:text-white transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {/* Glow ring on hover */}
        <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-emerald-400 to-teal-500 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-700 -z-10" />
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
          {item.title}
        </h3>
        {item.description && (
          <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
            {item.description}
          </p>
        )}
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Added{" "}
          {new Date(item.added_at).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </p>
      </div>
    </motion.div>
  );

  // ── Add card ───────────────────────────────────────────────
  const AddCard = () => (
    <motion.button
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      onClick={openAdd}
      disabled={gear.length >= 10}
      className={`relative rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-3 transition-all duration-300 ${
        gear.length >= 10
          ? "border-gray-200 dark:border-gray-800 text-gray-300 dark:text-gray-700 cursor-not-allowed"
          : "border-emerald-300 dark:border-emerald-700 text-emerald-500 dark:text-emerald-400 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:shadow-lg hover:shadow-emerald-500/10"
      }`}
      style={{ minHeight: "20rem" }}
    >
      <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
        <Plus className="w-7 h-7" />
      </div>
      <span className="font-semibold text-base">Add Gear</span>
      <span className="text-xs opacity-60">
        {gear.length}/10 items
      </span>
    </motion.button>
  );

  // ── Empty state ────────────────────────────────────────────
  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <div className="relative mb-6">
        <div className="w-28 h-28 rounded-full bg-gradient-to-br from-emerald-100 to-teal-50 dark:from-emerald-900/40 dark:to-gray-800 flex items-center justify-center">
          <ShieldAlert className="w-14 h-14 text-emerald-400 dark:text-emerald-600" />
        </div>
        <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
          <Plus className="w-5 h-5 text-white" />
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
        Your Armoury is Empty
      </h3>
      <p className="text-gray-500 dark:text-gray-400 max-w-md mb-6 leading-relaxed">
        Show off your cycling gear — bikes, helmets, wearables, accessories, and
        everything that makes you ride. Add up to 10 items!
      </p>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={openAdd}
        className="px-7 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-shadow"
      >
        <span className="flex items-center gap-2">
          <Plus className="w-5 h-5" /> Add Your First Gear
        </span>
      </motion.button>
    </motion.div>
  );

  // ═══════════════════════════════════════════════════════════
  //                        RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div>
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20">
            <Swords className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Armoury
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Your personal gear showcase
            </p>
          </div>
        </div>

        {gear.length > 0 && gear.length < 10 && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={openAdd}
            className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium shadow-md hover:shadow-lg transition-shadow text-sm"
          >
            <Plus className="w-4 h-4" /> Add Gear
          </motion.button>
        )}
      </div>

      {/* ── Grid / Empty / Loading ─────────────────────────── */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <ShimmerCard key={i} />
          ))}
        </div>
      ) : gear.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {gear.map((item, i) => (
              <GearCard key={item._id} item={item} index={i} />
            ))}
          </AnimatePresence>
          <AddCard />
        </div>
      )}

      {/* ═══ FULL SCREEN IMAGE MODAL ════════════════════════ */}
      <AnimatePresence>
        {fullScreenImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
            onClick={() => setFullScreenImage(null)}
          >
            <button
              onClick={() => setFullScreenImage(null)}
              className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              src={fullScreenImage}
              alt="Full screen preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ ADD / EDIT MODAL ═══════════════════════════════ */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            key="modal-bg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={closeModal}
          >
            <motion.div
              key="modal"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ type: "spring", damping: 24, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-2">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editItem ? "Edit Gear" : "Add New Gear"}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 pt-4 space-y-5">
                {/* Image picker */}
                <div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    ref={fileRef}
                    className="hidden"
                    onChange={handleImageChange}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-full h-44 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center gap-2 hover:border-emerald-400 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-colors overflow-hidden group relative"
                  >
                    {imagePreview ? (
                      <>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                          <span className="text-white font-medium flex items-center gap-2">
                            <Pencil className="w-4 h-4" /> Change Image
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <ImagePlus className="w-10 h-10 text-gray-300 dark:text-gray-600 group-hover:text-emerald-400 transition-colors" />
                        <span className="text-sm text-gray-400 dark:text-gray-500">
                          Click to {editItem ? 'change' : 'add'} an image (optional)
                        </span>
                      </>
                    )}
                  </button>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Title <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Trek Madone SLR"
                    maxLength={60}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">
                    {title.length}/60
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Description{" "}
                    <span className="text-gray-400 font-normal">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Carbon frame, Shimano Di2, 7.5 kg..."
                    maxLength={200}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">
                    {description.length}/200
                  </p>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting || !title.trim()}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold shadow-lg hover:shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : editItem ? (
                    "Save Changes"
                  ) : (
                    "Add to Armoury"
                  )}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ DELETE CONFIRMATION ═════════════════════════════ */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            key="del-bg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setDeleteTarget(null)}
          >
            <motion.div
              key="del-modal"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 text-center"
            >
              <div className="w-14 h-14 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <Trash2 className="w-7 h-7 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                Remove Gear?
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                "{deleteTarget.title}" will be permanently removed from your
                armoury.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Armoury;
